import { useState, useEffect } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { shiftInsightsAPI, organizationsAPI, locationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    Sun,
    Utensils,
    Moon,
    Coffee,
    Calendar,
    Clock,
    TrendingUp,
    TrendingDown,
    Users,
    Building2,
    MapPin,
    Trophy,
    Info,
    Lightbulb,
    AlertCircle,
    CheckCircle,
    Loader2,
    HelpCircle,
    Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';

const SHIFT_ICONS = {
    breakfast: Coffee,
    lunch: Utensils,
    dinner: Moon,
    late_night: Moon,
    other: Clock
};

const SHIFT_COLORS = {
    breakfast: '#f59e0b',  // Amber
    lunch: '#10b981',      // Emerald
    dinner: '#6366f1',     // Indigo
    late_night: '#8b5cf6', // Purple
    other: '#6b7280'       // Gray
};

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Help tooltip component for user-friendly design
const HelpTooltip = ({ text }) => (
    <TooltipProvider>
        <Tooltip>
            <TooltipTrigger asChild>
                <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help inline-block ml-1" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
                <p className="text-sm">{text}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

export default function ShiftInsights() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [locations, setLocations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [selectedShift, setSelectedShift] = useState('');
    const [showSettings, setShowSettings] = useState(false);
    const [shiftTimes, setShiftTimes] = useState({
        breakfast_start: 6, breakfast_end: 11,
        lunch_start: 11, lunch_end: 15,
        dinner_start: 17, dinner_end: 22,
        late_night_start: 22, late_night_end: 2
    });
    const [savingSettings, setSavingSettings] = useState(false);

    const isSuperAdmin = () => user?.role === 'super_admin';
    const isClientAdmin = () => user?.role === 'client_admin';

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        if (organizations.length > 0 || !isSuperAdmin()) {
            loadShiftData();
            loadLeaderboard();
        }
    }, [selectedOrg, selectedLocation]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            
            if (isSuperAdmin()) {
                const orgsRes = await organizationsAPI.list();
                setOrganizations(sortByName(orgsRes.data));
            }
            
            const locsRes = await locationsAPI.list();
            setLocations(sortByName(locsRes.data));
            
            await loadShiftData();
            await loadLeaderboard();
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const loadShiftData = async () => {
        try {
            const params = {};
            if (selectedOrg && selectedOrg !== '__all__') params.organization_id = selectedOrg;
            if (selectedLocation && selectedLocation !== '__all__') params.location_id = selectedLocation;
            
            const res = await shiftInsightsAPI.getSummary(params);
            setData(res.data);
        } catch (error) {
            console.error('Failed to load shift data:', error);
        }
    };

    const loadLeaderboard = async () => {
        try {
            const params = {};
            if (selectedOrg && selectedOrg !== '__all__') params.organization_id = selectedOrg;
            if (selectedLocation && selectedLocation !== '__all__') params.location_id = selectedLocation;
            if (selectedShift && selectedShift !== '__all__') params.shift_type = selectedShift;
            
            const res = await shiftInsightsAPI.getLeaderboard(params);
            setLeaderboard(res.data.leaderboard);
        } catch (error) {
            console.error('Failed to load leaderboard:', error);
        }
    };

    const loadShiftTimes = async () => {
        try {
            const orgId = selectedOrg || user?.organization_id;
            if (!orgId) return;
            
            const res = await shiftInsightsAPI.getShiftTimes(orgId);
            setShiftTimes(res.data.shift_times);
        } catch (error) {
            console.error('Failed to load shift times:', error);
        }
    };

    const saveShiftTimes = async () => {
        setSavingSettings(true);
        try {
            const orgId = selectedOrg || user?.organization_id;
            await shiftInsightsAPI.updateShiftTimes(orgId, shiftTimes);
            toast.success('Shift times updated successfully!');
            setShowSettings(false);
            loadShiftData();
        } catch (error) {
            toast.error('Failed to save shift times');
        } finally {
            setSavingSettings(false);
        }
    };

    const formatHour = (hour) => {
        if (hour === 0) return '12 AM';
        if (hour === 12) return '12 PM';
        if (hour < 12) return `${hour} AM`;
        return `${hour - 12} PM`;
    };

    const getInsightIcon = (type) => {
        switch (type) {
            case 'positive': return <CheckCircle className="w-5 h-5 text-emerald-500" />;
            case 'attention': return <AlertCircle className="w-5 h-5 text-amber-500" />;
            default: return <Info className="w-5 h-5 text-blue-500" />;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
        );
    }

    // Prepare chart data
    const shiftChartData = data?.shift_performance?.map(s => ({
        name: s.shift_label,
        taps: s.total_taps,
        avgPerDay: s.avg_taps_per_day,
        roi: s.roi_value,
        fill: SHIFT_COLORS[s.shift_type]
    })) || [];

    const dayTypeChartData = data?.day_type_performance?.map(d => ({
        name: d.day_label,
        taps: d.total_taps,
        avgPerDay: d.avg_taps_per_day
    })) || [];

    return (
        <div className="space-y-6" data-testid="shift-insights">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                        <Clock className="w-7 h-7 text-emerald-500" />
                        {t('shiftInsights.title')}
                        <HelpTooltip text={t('shiftInsights.titleHint', { defaultValue: 'See how your team performs during different times of day - Breakfast, Lunch, Dinner, and Late Night shifts.' })} />
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {t('shiftInsights.subtitle', { defaultValue: 'Understand when your team generates the most reviews' })}
                    </p>
                </div>
                
                <div className="flex items-center gap-3">
                    {(isSuperAdmin() || isClientAdmin()) && (
                        <Button variant="outline" onClick={() => { loadShiftTimes(); setShowSettings(true); }}>
                            <Settings className="w-4 h-4 mr-2" />
                            {t('shiftInsights.shiftTimes')}
                        </Button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-wrap gap-4">
                        {isSuperAdmin() && (
                            <div className="min-w-[200px]">
                                <Label className="text-sm text-muted-foreground mb-1 block">
                                    Organization
                                    <HelpTooltip text="Filter data by a specific organization" />
                                </Label>
                                <Select value={selectedOrg || '__all__'} onValueChange={(v) => setSelectedOrg(v === '__all__' ? '' : v)}>
                                    <SelectTrigger>
                                        <Building2 className="w-4 h-4 mr-2 text-muted-foreground" />
                                        <SelectValue placeholder={t('shiftInsights.allOrganizations')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">{t('shiftInsights.allOrganizations')}</SelectItem>
                                        {organizations.map(org => (
                                            <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        
                        <div className="min-w-[200px]">
                            <Label className="text-sm text-muted-foreground mb-1 block">
                                Location
                                <HelpTooltip text="Filter data by a specific location" />
                            </Label>
                            <Select value={selectedLocation || '__all__'} onValueChange={(v) => setSelectedLocation(v === '__all__' ? '' : v)}>
                                <SelectTrigger>
                                    <MapPin className="w-4 h-4 mr-2 text-muted-foreground" />
                                    <SelectValue placeholder={t('shiftInsights.allLocations')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all__">All Locations</SelectItem>
                                    {locations.map(loc => (
                                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {data?.shift_performance?.map(shift => {
                    const Icon = SHIFT_ICONS[shift.shift_type] || Clock;
                    return (
                        <Card key={shift.shift_type} className="relative overflow-hidden">
                            <div 
                                className="absolute top-0 left-0 w-1 h-full"
                                style={{ backgroundColor: SHIFT_COLORS[shift.shift_type] }}
                            />
                            <CardContent className="pt-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                                            <Icon className="w-4 h-4" />
                                            {shift.shift_label}
                                        </p>
                                        <p className="text-3xl font-bold mt-1">{shift.total_taps}</p>
                                        <p className="text-sm text-muted-foreground">{t('shiftInsights.reviewsGenerated')}</p>
                                    </div>
                                    <div className="text-right">
                                        <Badge 
                                            variant="secondary" 
                                            className="mb-2"
                                            style={{ backgroundColor: `${SHIFT_COLORS[shift.shift_type]}20`, color: SHIFT_COLORS[shift.shift_type] }}
                                        >
                                            {shift.avg_taps_per_day}/day
                                        </Badge>
                                        {shift.roi_value > 0 && (
                                            <p className="text-sm font-semibold text-emerald-600">${shift.roi_value}</p>
                                        )}
                                    </div>
                                </div>
                                {shift.top_employee && (
                                    <div className="mt-4 pt-3 border-t text-sm">
                                        <span className="text-muted-foreground">{t('shiftInsights.topPerformer')}</span>
                                        <span className="ml-2 font-medium text-foreground">{shift.top_employee}</span>
                                        <Badge variant="outline" className="ml-2">{shift.top_employee_taps}</Badge>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Insights */}
            {data?.insights && data.insights.length > 0 && (
                <Card className="bg-gradient-to-r from-blue-50 to-emerald-50 border-none">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Lightbulb className="w-5 h-5 text-amber-500" />
                            Smart Insights
                            <HelpTooltip text="Automatic recommendations based on your performance data" />
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {data.insights.map((insight, idx) => (
                                <div key={idx} className="flex items-start gap-3 bg-background p-3 rounded-lg shadow-sm">
                                    {getInsightIcon(insight.type)}
                                    <p className="text-foreground">{insight.message_key ? t(insight.message_key, { ...(insight.message_vars || {}), defaultValue: insight.message }) : insight.message}</p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            <Tabs defaultValue="comparison" className="space-y-6">
                <TabsList>
                    <TabsTrigger value="comparison">Shift Comparison</TabsTrigger>
                    <TabsTrigger value="heatmap">{t('shiftInsights.activityHeatmap')}</TabsTrigger>
                    <TabsTrigger value="leaderboard">{t('shiftInsights.shiftLeaderboard')}</TabsTrigger>
                </TabsList>

                {/* Shift Comparison Tab */}
                <TabsContent value="comparison">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">
                                    {t('shiftInsights.reviewsByShift')}
                                    <HelpTooltip text={t('shiftInsights.reviewsByShiftHint')} />
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={shiftChartData}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <RechartsTooltip 
                                            formatter={(value, name) => [value, name === 'taps' ? 'Total Reviews' : name]}
                                        />
                                        <Bar dataKey="taps" radius={[4, 4, 0, 0]}>
                                            {shiftChartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">
                                    {t('shiftInsights.weekdayVsWeekend')}
                                    <HelpTooltip text="See if your team performs better on weekdays (Mon-Thu) or weekends (Fri-Sun)" />
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={dayTypeChartData} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                        <XAxis type="number" />
                                        <YAxis dataKey="name" type="category" width={120} />
                                        <RechartsTooltip />
                                        <Bar dataKey="taps" fill="#10b981" radius={[0, 4, 4, 0]} name="Total Reviews" />
                                    </BarChart>
                                </ResponsiveContainer>
                                
                                <div className="mt-4 flex justify-center gap-8">
                                    {dayTypeChartData.map((d) => (
                                        <div key={d.name} className="text-center">
                                            <p className="text-2xl font-bold text-foreground">{d.avgPerDay}</p>
                                            <p className="text-sm text-muted-foreground">avg/day on {d.name.split(' ')[0]}</p>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Heatmap Tab */}
                <TabsContent value="heatmap">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                {t('shiftInsights.activityHeatmap')}
                                <HelpTooltip text={t('shiftInsights.activityHeatmapHint', { defaultValue: 'Visual grid showing when reviews are generated - darker colors mean more activity' })} />
                            </CardTitle>
                            <CardDescription>
                                {t('shiftInsights.activityHeatmapDesc', { defaultValue: 'Darker cells indicate more reviews during that time' })}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <div className="min-w-[600px]">
                                    {/* Hour labels */}
                                    <div className="flex mb-2">
                                        <div className="w-16"></div>
                                        {[6, 9, 12, 15, 18, 21].map(hour => (
                                            <div key={hour} className="flex-1 text-center text-xs text-muted-foreground">
                                                {formatHour(hour)}
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* Heatmap grid */}
                                    {DAY_NAMES.map((day, dayIdx) => (
                                        <div key={day} className="flex items-center mb-1">
                                            <div className="w-16 text-sm text-muted-foreground font-medium">{day}</div>
                                            <div className="flex-1 flex gap-[2px]">
                                                {Array.from({ length: 24 }, (_, hour) => {
                                                    const cell = data?.heatmap?.find(h => h.day === dayIdx && h.hour === hour);
                                                    const intensity = cell?.intensity || 0;
                                                    return (
                                                        <TooltipProvider key={hour}>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <div
                                                                        className="h-8 flex-1 rounded-sm cursor-pointer transition-transform hover:scale-110"
                                                                        style={{
                                                                            backgroundColor: intensity > 0 
                                                                                ? `rgba(16, 185, 129, ${Math.max(0.1, intensity)})` 
                                                                                : '#f3f4f6'
                                                                        }}
                                                                    />
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p className="font-medium">{day} at {formatHour(hour)}</p>
                                                                    <p className="text-sm">{cell?.tap_count || 0} reviews</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {/* Legend */}
                                    <div className="flex items-center justify-center gap-2 mt-4 text-sm text-muted-foreground">
                                        <span>{t('shiftInsights.less')}</span>
                                        <div className="flex gap-1">
                                            {[0.1, 0.3, 0.5, 0.7, 1].map(opacity => (
                                                <div 
                                                    key={opacity}
                                                    className="w-6 h-6 rounded-sm"
                                                    style={{ backgroundColor: `rgba(16, 185, 129, ${opacity})` }}
                                                />
                                            ))}
                                        </div>
                                        <span>{t('shiftInsights.more')}</span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Leaderboard Tab */}
                <TabsContent value="leaderboard">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <Trophy className="w-5 h-5 text-yellow-500" />
                                        {t('shiftInsights.shiftLeaderboard')}
                                        <HelpTooltip text={t('shiftInsights.shiftLeaderboardHint', { defaultValue: "See your top performers for each shift - find out who's crushing it at lunch vs dinner!" })} />
                                    </CardTitle>
                                    <CardDescription>{t('shiftInsights.topPerformers')}</CardDescription>
                                </div>
                                <Select value={selectedShift || '__all__'} onValueChange={(v) => { setSelectedShift(v === '__all__' ? '' : v); }}>
                                    <SelectTrigger className="w-40">
                                        <SelectValue placeholder={t('shiftInsights.allShifts')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">{t('shiftInsights.allShifts')}</SelectItem>
                                        <SelectItem value="breakfast">Breakfast</SelectItem>
                                        <SelectItem value="lunch">Lunch</SelectItem>
                                        <SelectItem value="dinner">Dinner</SelectItem>
                                        <SelectItem value="late_night">Late Night</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <Button variant="outline" size="sm" className="mb-4" onClick={loadLeaderboard}>
                                Refresh Leaderboard
                            </Button>
                            
                            {leaderboard.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>{t('shiftInsights.noLeaderboardData')}</p>
                                    <p className="text-sm mt-1">Reviews will appear here as they&apos;re generated</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-16">Rank</TableHead>
                                            <TableHead>Employee</TableHead>
                                            <TableHead>Location</TableHead>
                                            <TableHead>Shift</TableHead>
                                            <TableHead className="text-right">Reviews</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {leaderboard.map(entry => (
                                            <TableRow key={`${entry.employee_id}-${entry.shift_type}`}>
                                                <TableCell>
                                                    {entry.rank === 1 && <span className="text-2xl">🥇</span>}
                                                    {entry.rank === 2 && <span className="text-2xl">🥈</span>}
                                                    {entry.rank === 3 && <span className="text-2xl">🥉</span>}
                                                    {entry.rank > 3 && <span className="text-muted-foreground">#{entry.rank}</span>}
                                                </TableCell>
                                                <TableCell className="font-medium">{entry.employee_name}</TableCell>
                                                <TableCell className="text-muted-foreground">{entry.location_name || '-'}</TableCell>
                                                <TableCell>
                                                    <Badge 
                                                        variant="outline"
                                                        style={{ borderColor: SHIFT_COLORS[entry.shift_type], color: SHIFT_COLORS[entry.shift_type] }}
                                                    >
                                                        {entry.shift_label}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right font-bold">{entry.tap_count}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Shift Times Settings Dialog */}
            <Dialog open={showSettings} onOpenChange={setShowSettings}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            {t('shiftInsights.configureShiftTimes')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('shiftInsights.configureDesc', { defaultValue: 'Customize when each shift starts and ends for accurate performance tracking.' })}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-6 py-4">
                        {/* Breakfast */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Coffee className="w-4 h-4 text-amber-500" />
                                Breakfast
                            </Label>
                            <div className="flex items-center gap-2">
                                <Select 
                                    value={shiftTimes.breakfast_start.toString()} 
                                    onValueChange={(v) => setShiftTimes({...shiftTimes, breakfast_start: parseInt(v)})}
                                >
                                    <SelectTrigger className="w-28">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({length: 12}, (_, i) => i + 4).map(h => (
                                            <SelectItem key={h} value={h.toString()}>{formatHour(h)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span className="text-muted-foreground">to</span>
                                <Select 
                                    value={shiftTimes.breakfast_end.toString()} 
                                    onValueChange={(v) => setShiftTimes({...shiftTimes, breakfast_end: parseInt(v)})}
                                >
                                    <SelectTrigger className="w-28">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({length: 8}, (_, i) => i + 9).map(h => (
                                            <SelectItem key={h} value={h.toString()}>{formatHour(h)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Lunch */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Utensils className="w-4 h-4 text-emerald-500" />
                                Lunch
                            </Label>
                            <div className="flex items-center gap-2">
                                <Select 
                                    value={shiftTimes.lunch_start.toString()} 
                                    onValueChange={(v) => setShiftTimes({...shiftTimes, lunch_start: parseInt(v)})}
                                >
                                    <SelectTrigger className="w-28">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({length: 6}, (_, i) => i + 10).map(h => (
                                            <SelectItem key={h} value={h.toString()}>{formatHour(h)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span className="text-muted-foreground">to</span>
                                <Select 
                                    value={shiftTimes.lunch_end.toString()} 
                                    onValueChange={(v) => setShiftTimes({...shiftTimes, lunch_end: parseInt(v)})}
                                >
                                    <SelectTrigger className="w-28">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({length: 6}, (_, i) => i + 13).map(h => (
                                            <SelectItem key={h} value={h.toString()}>{formatHour(h)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Dinner */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Moon className="w-4 h-4 text-indigo-500" />
                                Dinner
                            </Label>
                            <div className="flex items-center gap-2">
                                <Select 
                                    value={shiftTimes.dinner_start.toString()} 
                                    onValueChange={(v) => setShiftTimes({...shiftTimes, dinner_start: parseInt(v)})}
                                >
                                    <SelectTrigger className="w-28">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({length: 6}, (_, i) => i + 15).map(h => (
                                            <SelectItem key={h} value={h.toString()}>{formatHour(h)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span className="text-muted-foreground">to</span>
                                <Select 
                                    value={shiftTimes.dinner_end.toString()} 
                                    onValueChange={(v) => setShiftTimes({...shiftTimes, dinner_end: parseInt(v)})}
                                >
                                    <SelectTrigger className="w-28">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({length: 6}, (_, i) => i + 20).map(h => (
                                            <SelectItem key={h} value={h.toString()}>{formatHour(h)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Late Night */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Moon className="w-4 h-4 text-purple-500" />
                                Late Night
                            </Label>
                            <div className="flex items-center gap-2">
                                <Select 
                                    value={shiftTimes.late_night_start.toString()} 
                                    onValueChange={(v) => setShiftTimes({...shiftTimes, late_night_start: parseInt(v)})}
                                >
                                    <SelectTrigger className="w-28">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({length: 6}, (_, i) => i + 20).map(h => (
                                            <SelectItem key={h} value={h.toString()}>{formatHour(h)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span className="text-muted-foreground">to</span>
                                <Select 
                                    value={shiftTimes.late_night_end.toString()} 
                                    onValueChange={(v) => setShiftTimes({...shiftTimes, late_night_end: parseInt(v)})}
                                >
                                    <SelectTrigger className="w-28">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from({length: 6}, (_, i) => i).map(h => (
                                            <SelectItem key={h} value={h.toString()}>{formatHour(h)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSettings(false)}>
                            Cancel
                        </Button>
                        <Button onClick={saveShiftTimes} disabled={savingSettings}>
                            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

